import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { useHelpSearch } from '@/hooks/useHelp';
import { useTranslation } from 'react-i18next';

interface HelpSearchProps {
  placeholder?: string;
  autoFocus?: boolean;
}

export function HelpSearch({ placeholder, autoFocus = false }: HelpSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const searchQuery = useHelpSearch(query);
  const results = searchQuery.data ?? [];

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          navigate(`/help/${results[selectedIndex].category_slug}/${results[selectedIndex].slug}`);
          setShowResults(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setShowResults(false);
        break;
    }
  };

  const handleSelectResult = (categorySlug: string, slug: string) => {
    navigate(`/help/${categorySlug}/${slug}`);
    setShowResults(false);
    setQuery('');
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-ds-amber-tint text-ds-amber-ink">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowResults(e.target.value.trim().length >= 2);
        }}
        onFocus={() => query.trim().length >= 2 && setShowResults(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? t('help.search_placeholder', 'Søg i hjælpecenteret...')}
        leadingIcon={<Search className="h-4 w-4" aria-hidden />}
        trailingIcon={
          query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setShowResults(false);
              }}
              className="hover:text-ds-text"
            >
              <X className="h-4 w-4" />
            </button>
          ) : undefined
        }
      />

      {showResults && query.trim().length >= 2 && (
        <div
          ref={resultsRef}
          className="absolute z-50 mt-2 w-full rounded-lg border border-ds-line-strong bg-ds-card shadow-pop"
        >
          {searchQuery.isLoading ? (
            <div className="p-4 text-center text-sm text-ds-muted">
              {t('help.searching', 'Søger...')}
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-ds-muted">
              {t('help.no_results', 'Ingen resultater fundet')}
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto py-2">
              {results.map((result, idx) => (
                <li key={result.id}>
                  <button
                    onClick={() => handleSelectResult(result.category_slug, result.slug)}
                    className={`block w-full px-4 py-3 text-left transition-colors ${
                      idx === selectedIndex ? 'bg-ds-teal-tint' : 'hover:bg-ds-row-hover'
                    }`}
                  >
                    <div className="text-sm font-medium text-ds-text">
                      {highlightMatch(result.title, query)}
                    </div>
                    <div className="mt-1 text-xs text-ds-muted">
                      {highlightMatch(result.excerpt ?? '', query)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
